import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { isValidSource } from "@/utils/validation";
import { CompanyDTO } from "@shaarp/common";
import { InfoIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Sources,
  SourcesTrigger,
  SourcesContent,
  Source,
} from "../ai-elements/sources";

type Props = {
  company: CompanyDTO;
};

/**
 * Cell component displaying the AI-generated reason and sources
 * for why a company was included in search results.
 * Uses Sources component for a collapsible list of citations.
 */
export const DataTableCellCompanyAiReason = ({ company }: Props) => {
  const { t } = useTranslation("components");
  const reason = company.aiSearchReason;
  const reasonKey = company.aiSearchReasonKey;
  const rawSources = company.aiSearchSources ?? [];
  const sources = rawSources.filter(isValidSource);

  if (!reason) return null;

  const tooltipKey = `dataTableCompanies.columns.aiReasonTooltip.${reasonKey}`;
  const tooltip = reasonKey ? t(tooltipKey, { defaultValue: "" }) : "";

  return (
    <div className="min-w-[180px] max-w-xs space-y-1">
      <div className="flex items-start gap-1">
        <p className="text-sm text-muted-foreground">{reason}</p>
        {tooltip && (
          <Tooltip>
            <TooltipTrigger asChild>
              <InfoIcon className="size-3.5 shrink-0 mt-0.5 text-muted-foreground/60 cursor-help" />
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <p className="text-xs">{tooltip}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      {sources.length > 0 && (
        <Sources>
          <SourcesTrigger count={sources.length} />
          <SourcesContent>
            {sources.map((source, i) => (
              <Source
                key={i}
                href={source.url}
                title={source.title}
              />
            ))}
          </SourcesContent>
        </Sources>
      )}
    </div>
  );
};
